import {
  type DataUploadConversion,
  dataUploadSkipReasonKey,
  dataUploadSkippedRowKey,
  downloadDataUploadTemplate,
  wasReadByAi,
} from 'common/helpers/dataUpload'
import {AlertTriangle, ChevronDown, ChevronRight, Download, Sparkles, X} from 'lucide-react'
import React, {FC, useId, useState} from 'react'
import {useIntl} from 'react-intl'

interface Props {
  conversion: DataUploadConversion
  isDark: boolean
  /** Students: adds the "N new students added to your M existing ones" sentence. */
  showKeptStudents?: boolean
  /** Shown as an "x" when given; the report is informational and may be put away. */
  onDismiss?: () => void
  className?: string
}

/** Rows listed before the "...and N more" line; a 1,000-row roster must not flood the page. */
const MAX_LISTED_ROWS = 50

/**
 * What a conversion did, shown after every "any file -> our template" upload and BEFORE anything
 * is saved: "Read 1,146 · Added 1,144 · Left out 2 · Check 32", with the left-out and
 * worth-checking rows as expandable lists, an "AI-read" badge when a photo, scan or free text
 * was read by AI, and the filled template as a download (fix it in Excel, upload it again: a
 * filled template is never read by AI).
 *
 * Renders nothing for a passthrough with nothing to report (the file already was our template).
 */
const DataUploadReport: FC<Props> = ({
  conversion,
  isDark,
  showKeptStudents = false,
  onDismiss,
  className,
}) => {
  const intl = useIntl()
  const listId = useId()
  const [open, setOpen] = useState<'skipped' | 'check' | null>(null)

  const {report, template, passthrough} = conversion
  const aiRead = wasReadByAi(conversion)
  if (passthrough && report.skipped.length === 0 && report.needsReview.length === 0) return null

  const colors = isDark
    ? {bg: '#1b1d24', border: '#2f3240', text: '#e4e6ef', muted: '#92929f', chip: '#262936'}
    : {bg: '#f8fafc', border: '#e4e6ef', text: '#181c32', muted: '#5e6278', chip: '#eef2f7'}

  const chip = (label: string, tone?: 'warn') => (
    <span
      style={{
        background: tone === 'warn' ? 'rgba(245, 158, 11, 0.16)' : colors.chip,
        color: tone === 'warn' ? (isDark ? '#fbbf24' : '#92600a') : colors.text,
        borderRadius: 999,
        padding: '0.2rem 0.7rem',
        fontSize: '0.85rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  )

  const toggle = (
    key: 'skipped' | 'check',
    label: string,
    count: number,
    tone?: 'warn'
  ): React.ReactNode => {
    if (count === 0) return chip(label)
    const expanded = open === key
    return (
      <button
        type='button'
        className='btn btn-link p-0 border-0 d-inline-flex align-items-center gap-1'
        style={{textDecoration: 'none'}}
        aria-expanded={expanded}
        aria-controls={`${listId}-${key}`}
        onClick={() => setOpen(expanded ? null : key)}
      >
        {chip(label, tone)}
        {expanded ? (
          <ChevronDown size={14} color={colors.muted} aria-hidden />
        ) : (
          <ChevronRight size={14} color={colors.muted} aria-hidden />
        )}
      </button>
    )
  }

  const unnamed = intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.UNNAMED_ROW'})
  const moreLine = (total: number) =>
    total > MAX_LISTED_ROWS ? (
      <li style={{color: colors.muted, listStyle: 'none'}}>
        {intl.formatMessage(
          {id: 'DATA_UPLOAD.CONVERT.MORE_ROWS'},
          {count: total - MAX_LISTED_ROWS}
        )}
      </li>
    ) : null

  return (
    <div
      className={className}
      role='status'
      style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: '0.9rem 1rem',
        color: colors.text,
        textAlign: 'start',
      }}
    >
      <div className='d-flex align-items-start justify-content-between gap-3'>
        <div className='d-flex flex-wrap align-items-center gap-2'>
          <strong style={{fontSize: '0.95rem'}}>
            {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.SUMMARY.TITLE'})}
          </strong>
          {aiRead && (
            <span
              className='d-inline-flex align-items-center gap-1'
              style={{
                background: 'rgba(5, 110, 233, 0.14)',
                color: isDark ? '#8ec0ff' : '#056ee9',
                borderRadius: 999,
                padding: '0.15rem 0.6rem',
                fontSize: '0.78rem',
                fontWeight: 700,
              }}
            >
              <Sparkles size={12} aria-hidden />
              {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.AI_BADGE'})}
            </span>
          )}
        </div>
        {onDismiss && (
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            aria-label={intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.DISMISS'})}
            onClick={onDismiss}
            style={{width: 26, height: 26, flexShrink: 0}}
          >
            <X size={14} aria-hidden />
          </button>
        )}
      </div>

      <div className='d-flex flex-wrap align-items-center gap-2 mt-2'>
        {report.read !== null &&
          chip(intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.SUMMARY.READ'}, {count: report.read}))}
        {chip(
          intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.SUMMARY.ADDED'}, {count: report.written})
        )}
        {toggle(
          'skipped',
          intl.formatMessage(
            {id: 'DATA_UPLOAD.CONVERT.SUMMARY.LEFT_OUT'},
            {count: report.skipped.length}
          ),
          report.skipped.length,
          'warn'
        )}
        {toggle(
          'check',
          intl.formatMessage(
            {id: 'DATA_UPLOAD.CONVERT.SUMMARY.CHECK'},
            {count: report.needsReview.length}
          ),
          report.needsReview.length,
          'warn'
        )}
      </div>

      {/* The server's own summary ("…Please fill in the blanks: 5 need a class"). */}
      {conversion.message && (
        <p className='mb-0 mt-2' style={{fontSize: '0.88rem'}}>
          {conversion.message}
        </p>
      )}

      {showKeptStudents && report.kept > 0 && (
        <p className='mb-0 mt-2' style={{fontSize: '0.88rem'}}>
          {intl.formatMessage(
            {id: 'DATA_UPLOAD.CONVERT.SUMMARY.KEPT_STUDENTS'},
            {written: report.written, kept: report.kept}
          )}
        </p>
      )}

      {aiRead && (
        <p
          className='mb-0 mt-2 d-flex align-items-start gap-2'
          style={{fontSize: '0.88rem', color: isDark ? '#fbbf24' : '#92600a'}}
        >
          <AlertTriangle size={15} aria-hidden style={{flexShrink: 0, marginTop: 2}} />
          {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.AI_NOTE'})}
        </p>
      )}

      {open === 'skipped' && (
        <div id={`${listId}-skipped`} className='mt-3'>
          <div style={{fontSize: '0.82rem', fontWeight: 700, color: colors.muted}}>
            {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.LEFT_OUT_TITLE'})}
          </div>
          <ul
            className='mb-0 mt-1 ps-4'
            style={{fontSize: '0.88rem', maxHeight: 220, overflowY: 'auto'}}
          >
            {report.skipped.slice(0, MAX_LISTED_ROWS).map((row, index) => {
              // The row's own code ("NAME_NOT_ENGLISH" under reason INVALID) is translated
              // first. Otherwise the backend message is specific ("Registration number
              // FCD-2740 is on more than one row..."); the translated reason is the fallback.
              const codeKey = row.code ? dataUploadSkippedRowKey({code: row.code}) : null
              const reasonKey = dataUploadSkipReasonKey(row.reason)
              const detail = codeKey
                ? intl.formatMessage({id: codeKey})
                : row.message || (reasonKey ? intl.formatMessage({id: reasonKey}) : row.reason)
              return (
                <li key={row.recordId ?? `${row.label}-${index}`}>
                  <strong>{row.label || unnamed}</strong>
                  {detail ? <span style={{color: colors.muted}}> · {detail}</span> : null}
                </li>
              )
            })}
            {moreLine(report.skipped.length)}
          </ul>
        </div>
      )}

      {open === 'check' && (
        <div id={`${listId}-check`} className='mt-3'>
          <div style={{fontSize: '0.82rem', fontWeight: 700, color: colors.muted}}>
            {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.CHECK_TITLE'})}
          </div>
          <ul
            className='mb-0 mt-1 ps-4'
            style={{fontSize: '0.88rem', maxHeight: 220, overflowY: 'auto'}}
          >
            {report.needsReview.slice(0, MAX_LISTED_ROWS).map((row, index) => (
              <li key={row.recordId ?? `${row.label}-${index}`}>
                <strong>{row.label || unnamed}</strong>
                {row.fields && row.fields.length > 0 ? (
                  <span style={{color: colors.muted}}>
                    {' · '}
                    {intl.formatMessage(
                      {id: 'DATA_UPLOAD.CONVERT.CHECK_FIELDS'},
                      {fields: row.fields.join(', ')}
                    )}
                  </span>
                ) : null}
              </li>
            ))}
            {moreLine(report.needsReview.length)}
          </ul>
        </div>
      )}

      <div className='d-flex flex-wrap align-items-center gap-2 mt-3'>
        {template && (
          <button
            type='button'
            className='btn btn-sm btn-light-primary d-inline-flex align-items-center gap-2'
            onClick={() => downloadDataUploadTemplate(template)}
          >
            <Download size={14} aria-hidden />
            {intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.DOWNLOAD_TEMPLATE'})}
          </button>
        )}
        <span style={{fontSize: '0.82rem', color: colors.muted}}>
          {intl.formatMessage({
            id: template
              ? 'DATA_UPLOAD.CONVERT.DOWNLOAD_TEMPLATE_HINT'
              : 'DATA_UPLOAD.CONVERT.SUMMARY.NOT_SAVED_YET',
          })}
        </span>
      </div>
    </div>
  )
}

export default DataUploadReport
